/**
 * Admin campaign management — list, create, edit, delete campaigns and view their chapters.
 */

import { formatDate } from '../../../utils/format-date';
import { localDateInput } from '../../../utils/date-input';
import { saveFailure, loadFailure, deleteFailure, type FormFailure } from '../save-failure-words';
import { EmptyState } from '../../../components/empty-state';
import { Modal } from '../../../components/modal';
import { Table } from '../../../components/table';
import { CheckRow, Checkbox, FormBlock, FormField, FormMessage, Input, Select } from '../../../components/form-field';
import { useState } from 'react';
import { Button } from '../../../components/button';
import { useTranslation } from 'react-i18next';
import { CommonHeader } from '../../shell/common-header';
import type {
  AdminCampaign,
  CreateCampaignPayload,
  UpdateCampaignPayload,
} from '../../../api/admin/campaigns/types';
import {
  useAdminCampaignsList,
  useCampaignTemplates,
  useCreateCampaign,
  useDeleteCampaign,
  useUpdateCampaign,
} from '../../../api/admin/campaigns/hooks';
import { useAdminGamesAll } from '../../../api/admin/games/hooks';
import { useAdminCohorts } from '../../../api/admin/cohorts/hooks';
import { CampaignDetail } from './campaign-detail';
import { CampaignStatusPill } from './campaign-words';
import { confirmDialog } from '../../../components/confirm-dialog/confirm-dialog-store';
import admin from '../admin-dashboard.module.css';

interface Props {
  /** Campaign whose detail to show; null shows the list. */
  campaignId: string | null;
  onOpenCampaign: (id: string) => void;
  onBack: () => void;
}

export function CampaignManagement({ campaignId, onOpenCampaign, onBack }: Props) {
  const { t } = useTranslation();
  const query = useAdminCampaignsList();
  const deleteCampaign = useDeleteCampaign();
  const campaigns: AdminCampaign[] = query.data ?? [];
  const loading = query.isLoading;
  const [pageError, setPageError] = useState<FormFailure | null>(null);
  const queryError = query.isError ? loadFailure(t, query.error, 'campaigns') : null;
  const error = pageError ?? queryError;
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<AdminCampaign | null>(null);

  async function handleDelete(c: AdminCampaign) {
    if (!(await confirmDialog({ message: t('admin.campaigns.confirmDelete', { name: c.name }), tone: 'danger' }))) return;
    setPageError(null);
    try {
      await deleteCampaign.mutateAsync(c.id);
    } catch (e) {
      setPageError(deleteFailure(t, e, 'campaign'));
    }
  }

  if (campaignId) {
    return <CampaignDetail id={campaignId} onBack={onBack} />;
  }

  return (
    <div className="portal-shell">
      <CommonHeader title={t('admin.campaigns.title')} showBackButton onBackClick={onBack} />

      <main className={admin.adminPanelMain}>
        {error && <FormMessage title={error.title}>{error.detail}</FormMessage>}

        <div className={admin.adminTableWrapper}>
          <div className={admin.adminTableHeader}>
            <h2 className={admin.adminTableTitle}>
              {t('admin.campaigns.totalCount', { count: campaigns.length })}
            </h2>
            <Button type="button" onClick={() => setShowCreate(true)}
            >
              + {t('admin.campaigns.create')}
            </Button>
          </div>

          {loading ? (
            <div className={admin.adminLoading}>{t('common.loading')}</div>
          ) : campaigns.length === 0 ? (
            !error && <EmptyState icon="🗺️">{t('admin.campaigns.empty')}</EmptyState>
          ) : (
            <Table flush>
              <thead>
                <tr>
                  <th>{t('admin.campaigns.columns.name')}</th>
                  <th>{t('admin.campaigns.columns.template')}</th>
                  <th>{t('admin.campaigns.columns.game')}</th>
                  <th>{t('admin.campaigns.columns.status')}</th>
                  <th>{t('admin.campaigns.columns.cohorts')}</th>
                  <th>{t('admin.campaigns.columns.dates')}</th>
                  <th>{t('admin.campaigns.columns.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr key={c.id}>
                    <td>{c.name}</td>
                    <td>{c.templateKey}</td>
                    <td>{c.gameName}</td>
                    <td>
                      <CampaignStatusPill status={c.status} />
                    </td>
                    <td>{c.cohorts.map((co) => co.name).join(', ') || '—'}</td>
                    <td>
                      {c.startDateUtc ? formatDate(c.startDateUtc) : '—'}
                      {' → '}
                      {c.endDateUtc ? formatDate(c.endDateUtc) : '—'}
                    </td>
                    <td>
                      <div className={admin.adminRowActions}>
                        <Button type="button" variant="ghost" size="sm" onClick={() => onOpenCampaign(c.id)}
                        >
                          {t('admin.campaigns.manageChapters')}
                        </Button>
                        <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(c)}
                        >
                          {t('common.edit')}
                        </Button>
                        <Button type="button" variant="dangerQuiet" size="sm" onClick={() => void handleDelete(c)}
                        >
                          {t('common.delete')}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </main>

      {showCreate && (
        <CampaignForm
          onClose={() => setShowCreate(false)}
          onSaved={() => setShowCreate(false)}
        />
      )}

      {editing && (
        <CampaignForm
          campaign={editing}
          onClose={() => setEditing(null)}
          onSaved={() => setEditing(null)}
        />
      )}
    </div>
  );
}

interface FormProps {
  campaign?: AdminCampaign;
  onClose: () => void;
  onSaved: () => void;
}

function CampaignForm({ campaign, onClose, onSaved }: FormProps) {
  const { t } = useTranslation();
  const isEditing = !!campaign;

  const [name, setName] = useState(campaign?.name ?? '');
  const [templateKey, setTemplateKey] = useState(campaign?.templateKey ?? '');
  const [gameName, setGameName] = useState(campaign?.gameName ?? '');
  // The campaign's first and last local days, as the inputs hold them. The save
  // writes them in local time, the first's 00:00 and the last's 23:59:59, which
  // read back as the same days.
  const [startTime, setStartTime] = useState(campaign?.startDateUtc ? localDateInput(campaign.startDateUtc) : '');
  const [endTime, setEndTime] = useState(campaign?.endDateUtc ? localDateInput(campaign.endDateUtc) : '');
  const [selectedCohorts, setSelectedCohorts] = useState<string[]>(campaign?.cohorts.map((c) => c.name) ?? []);

  const templatesQuery = useCampaignTemplates(!isEditing);
  const gamesQuery = useAdminGamesAll(!isEditing);
  const cohortsQuery = useAdminCohorts();
  const templates = templatesQuery.data ?? [];
  const games = gamesQuery.data ?? [];
  const cohorts = cohortsQuery.data ?? [];
  const createCampaign = useCreateCampaign();
  const updateCampaign = useUpdateCampaign();
  const submitting = createCampaign.isPending || updateCampaign.isPending;
  const [error, setError] = useState<FormFailure | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      // Date-only inputs anchor at local 00:00 (start) / end-of-day (end).
      const isoStart = startTime ? new Date(`${startTime}T00:00:00`).toISOString() : null;
      const isoEnd = endTime ? new Date(`${endTime}T23:59:59`).toISOString() : null;
      if (isEditing && campaign) {
        const payload: UpdateCampaignPayload = {
          name: name !== campaign.name ? name : undefined,
          startTime: isoStart,
          endTime: isoEnd,
          cohorts: selectedCohorts,
        };
        await updateCampaign.mutateAsync({ id: campaign.id, payload });
      } else {
        const payload: CreateCampaignPayload = {
          name,
          templateKey,
          gameName,
          startTime: isoStart,
          endTime: isoEnd,
          cohorts: selectedCohorts,
        };
        await createCampaign.mutateAsync(payload);
      }
      onSaved();
    } catch (e) {
      // An end before a session of the campaign starts is refused, and said in the admin's words.
      setError(saveFailure(t, e, 'campaign'));
    }
  }

  function toggleCohort(name: string) {
    setSelectedCohorts((cur) => cur.includes(name) ? cur.filter((n) => n !== name) : [...cur, name]);
  }

  return (
    <Modal isOpen onClose={onClose} size="mdWide" disableBackdropClose title={isEditing ? t('admin.campaigns.edit') : t('admin.campaigns.create')}>
      {error && <FormMessage title={error.title}>{error.detail}</FormMessage>}
      <form onSubmit={handleSubmit}>
        <FormField label={t('admin.campaigns.form.name')} required>
          {(field) => (
            <Input {...field}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          )}
        </FormField>

        {!isEditing && (
          <>
            <FormField label={t('admin.campaigns.form.template')} required>
              {(field) => (
                <Select
                  {...field}
                  value={templateKey}
                  onChange={(e) => {
                    setTemplateKey(e.target.value);
                    const t = templates.find((tt) => tt.templateKey === e.target.value);
                    if (t && !gameName) setGameName(t.gameName);
                  }}
                  required
                >
                  <option value="">—</option>
                  {templates.map((tmpl) => (
                    <option key={tmpl.templateKey} value={tmpl.templateKey}>
                      {tmpl.name} ({tmpl.chapterCount} chapters, {tmpl.activityCount} activities)
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
            <FormField label={t('admin.campaigns.form.game')} required>
              {(field) => (
                <Select
                  {...field}
                  value={gameName}
                  onChange={(e) => setGameName(e.target.value)}
                  required
                >
                  <option value="">—</option>
                  {games.map((g) => (
                    <option key={g.id} value={g.name}>{g.name}</option>
                  ))}
                </Select>
              )}
            </FormField>
          </>
        )}

        <FormField label={t('admin.campaigns.form.startTime')}>
          {(field) => (
            <Input {...field} type="date" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
          )}
        </FormField>
        <FormField label={t('admin.campaigns.form.endTime')}>
          {(field) => (
            <Input {...field} type="date" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
          )}
        </FormField>

        <FormBlock label={t('admin.campaigns.form.cohorts')}>
          <div className="flex flex-wrap gap-1.5">
            {cohorts.map((c) => (
              <CheckRow key={c.id}>
                <Checkbox
                  checked={selectedCohorts.includes(c.name)}
                  onChange={() => toggleCohort(c.name)}
                />
                {c.name}
              </CheckRow>
            ))}
          </div>
        </FormBlock>

        <div className={admin.adminFormActions}>
          <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? t('common.loading') : (isEditing ? t('common.save') : t('common.create'))}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
